/**
 * Utility functions for displaying user information with privacy
 */

/**
 * Get the public display name for a user (alias instead of real name)
 * @param {Object} user - User object with name and displayName
 * @param {boolean} isOwnProfile - Whether viewing own profile (show real name)
 * @returns {string} - Display name to show
 */
export const getDisplayName = (user, isOwnProfile = false) => {
  if (!user) return 'Unknown User';
  
  // Show real name only on own profile or if no displayName set
  if (isOwnProfile || !user.displayName) {
    return user.name || 'User';
  }
  
  // Show alias/display name to others for privacy
  return user.displayName;
};

/**
 * Generate a default display name from full name
 * Example: "Priya Sharma" -> "Priya S."
 */
export const generateDefaultDisplayName = (fullName) => {
  if (!fullName) return '';
  
  const parts = fullName.trim().split(' ');
  if (parts.length === 1) return parts[0];
  
  const firstName = parts[0];
  const lastInitial = parts[parts.length - 1][0];
  return `${firstName} ${lastInitial}.`;
};

/**
 * Simulate profile picture upload (returns a blob URL)
 * In production, this would upload to cloud storage (S3, Cloudinary, etc.)
 */
export const uploadProfilePicture = async (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided'));
      return;
    }

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      reject(new Error('Invalid file type. Please upload JPG, PNG, or WebP'));
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      reject(new Error('File too large. Maximum size is 5MB'));
      return;
    }

    // Simulate upload delay
    setTimeout(() => {
      // Create blob URL for preview
      // In production, this would be replaced with actual cloud URL
      const blobUrl = URL.createObjectURL(file);
      resolve(blobUrl);
    }, 500);
  });
};

/**
 * Validate display name format
 */
export const validateDisplayName = (displayName) => {
  if (!displayName || displayName.trim().length === 0) {
    return { valid: false, error: 'Display name cannot be empty' };
  }

  if (displayName.trim().length < 2) {
    return { valid: false, error: 'Display name must be at least 2 characters' };
  }

  if (displayName.length > 30) {
    return { valid: false, error: 'Display name must be less than 30 characters' };
  }

  // Check for inappropriate characters (basic validation)
  const validPattern = /^[a-zA-Z0-9\s\-_.]+$/;
  if (!validPattern.test(displayName)) {
    return { valid: false, error: 'Display name contains invalid characters' };
  }

  return { valid: true, error: null };
};

